"use client";

/**
 * useScrollReveal — enterprise scroll-reveal hook.
 *
 * Каждый блок анимируется ОДИН РАЗ за браузерную сессию.
 * Флаг `practicum_reveal_<blockId>` хранится в sessionStorage.
 *
 * rootMargin по умолчанию "0px 0px -25% 0px":
 *   Сжимает зону наблюдения снизу на 25vh — анимация стартует только
 *   когда элемент вошёл в верхние 75% viewport = пользователь его точно видит.
 *   Не "первый пиксель во вьюпорте", а "явно в центральной видимой зоне".
 *
 * Три состояния:
 *   idle      — до попадания в viewport
 *   animating — первый показ в сессии
 *   done      — уже было в сессии (нет анимации, финальное состояние)
 */

import { useEffect, useRef, useState } from "react";

export type RevealState = "idle" | "animating" | "done";

export function useScrollReveal(
  blockId: string,
  threshold = 0.15,
  rootMargin = "0px 0px -25% 0px",
): {
  ref: (el: HTMLElement | null) => void;
  state: RevealState;
} {
  const [state, setState] = useState<RevealState>("idle");
  const elRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const key = `practicum_reveal_${blockId}`;

    if (
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(key) === "1"
    ) {
      setState("done");
      return;
    }

    const el = elRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (typeof sessionStorage !== "undefined") {
            sessionStorage.setItem(key, "1");
          }
          setState("animating");
          io.disconnect();
        }
      },
      { threshold, rootMargin },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [blockId, threshold, rootMargin]);

  const ref = (el: HTMLElement | null) => {
    elRef.current = el;
  };

  return { ref, state };
}
